import { useState, useEffect, useCallback } from 'react';
import { money, money2 } from '../utils/currency';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLicence } from '../contexts/LicenceContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { format, subDays, startOfMonth, startOfWeek } from 'date-fns';
import { toast } from 'sonner';
import {
  BarChart3, TrendingUp, Users, DollarSign, Calendar, Download, FileText,
  ArrowUpRight, ArrowDownRight, Minus, ShoppingBag, UserCheck, Ban, Scissors,
  CreditCard, Lock, Search, Heart, XCircle, ClipboardList, ArrowLeft,
  Receipt, Percent, Star, ChevronRight, AlertTriangle, Clock, CheckCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { value: 'day', label: 'Today', i18n: 'reports.periodToday' },
  { value: '7', label: 'Last 7 days', i18n: 'reports.periodLast7' },
  { value: '14', label: 'Last 14 days', i18n: 'reports.periodLast14' },
  { value: '30', label: 'Last 30 days', i18n: 'reports.periodLast30' },
  { value: 'week', label: 'This week', i18n: 'reports.periodThisWeek' },
  { value: 'month', label: 'This month', i18n: 'reports.periodThisMonth' },
  { value: 'custom', label: 'Custom range', i18n: 'reports.periodCustom' },
];

const PIE_COLORS = ['#556B2F', '#3E6641', '#D4A373', '#2E86C1', '#8E44AD', '#E2725B', '#1ABC9C', '#F39C12'];

// ─── Quick-view dashboard tabs (the original analytics) ───
const DASH_TABS = [
  { key: 'performance', label: 'Performance', i18n: 'reports.tabPerformance', icon: BarChart3, tier: 'business' },
  { key: 'treatments', label: 'Top Treatments', i18n: 'reports.tabTopTreatments', icon: Scissors, tier: 'business' },
  { key: 'staff', label: 'Team', i18n: 'reports.tabTeam', icon: Users, tier: 'business' },
  { key: 'payments', label: 'Payments', i18n: 'reports.tabPayments', icon: CreditCard, tier: 'starter' },
  { key: 'clients', label: 'Clients', i18n: 'reports.tabClients', icon: UserCheck, tier: 'premium' },
  { key: 'accountant', label: 'Accountant', i18n: 'reports.tabAccountant', icon: FileText, tier: 'business' },
  { key: 'reset', label: 'Reset', i18n: 'reports.tabReset', icon: AlertTriangle, tier: 'premium', adminOnly: true },
];

// ─── Fresha-style report catalog ───
const ICON_MAP = { DollarSign, ShoppingBag, Receipt, Percent, BarChart3, CreditCard, FileText, Heart, Calendar, ClipboardList, XCircle, Users, UserCheck, Scissors, Star };

const REPORT_CATALOG = [
  { key: 'sales_list', name: 'Sales list', desc: 'Complete listing of all sales transactions', category: 'sales', tier: 'business', icon: 'Receipt', endpoint: 'detail' },
  { key: 'discount_summary', name: 'Discount summary', desc: 'Overview of discounts granted and their impact on sales', category: 'sales', tier: 'business', icon: 'Percent', endpoint: 'detail' },
  { key: 'finance_summary', name: 'Finance summary', desc: 'High-level summary of sales, payments and liabilities', category: 'finance', tier: 'business', icon: 'BarChart3', endpoint: 'overview' },
  { key: 'payment_transactions', name: 'Payment transactions', desc: 'Detailed view of all payment transactions', category: 'finance', tier: 'business', icon: 'FileText', endpoint: 'detail' },
  { key: 'tips_summary', name: 'Tips summary', desc: 'Analysis of gratuity income by team member', category: 'finance', tier: 'business', icon: 'Heart', endpoint: 'detail' },
  { key: 'appointments_list', name: 'Appointments list', desc: 'Full list of scheduled appointments', category: 'appointments', tier: 'starter', icon: 'ClipboardList', endpoint: 'detail' },
  { key: 'cancellations_noshow', name: 'Cancellations & no-show summary', desc: 'Insight into appointment cancellations and no-shows', category: 'appointments', tier: 'business', icon: 'XCircle', endpoint: 'detail' },
  { key: 'staff_commissions', name: 'Commission report', desc: 'Staff commission earnings breakdown', category: 'team', tier: 'business', icon: 'Percent', endpoint: 'commissions' },
  { key: 'client_list', name: 'Client list', desc: 'Comprehensive list of all active clients with spend data', category: 'clients', tier: 'business', icon: 'Users', endpoint: 'detail' },
];

const CATALOG_CATEGORIES = [
  { key: 'all', label: 'All', i18n: 'reports.catAll' },
  { key: 'sales', label: 'Sales', i18n: 'reports.catSales' },
  { key: 'finance', label: 'Finance', i18n: 'reports.catFinance' },
  { key: 'appointments', label: 'Appointments', i18n: 'reports.catAppointments' },
  { key: 'team', label: 'Team', i18n: 'reports.catTeam' },
  { key: 'clients', label: 'Clients', i18n: 'reports.catClients' },
];

function Change({ value }) {
  if (!value || value === 0) return <span className="text-xs text-[#5C6656] flex items-center gap-0.5"><Minus size={10} /> 0%</span>;
  return value > 0
    ? <span className="text-xs text-[#3E6641] flex items-center gap-0.5"><ArrowUpRight size={10} /> {value}%</span>
    : <span className="text-xs text-[#E2725B] flex items-center gap-0.5"><ArrowDownRight size={10} /> {Math.abs(value)}%</span>;
}

function MetricCard({ label, value, change, sub, icon: Icon, color = '#556B2F' }) {
  return (
    <Card className="border-[#E5E7E1]">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          {Icon && <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}12` }}><Icon size={14} style={{ color }} /></div>}
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#5C6656]">{label}</span>
        </div>
        <p className="text-xl font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{value}</p>
        <div className="flex items-center gap-2 mt-1">
          {change !== undefined && change !== null && <Change value={change} />}
          {sub && <span className="text-[10px] text-[#5C6656]">{sub}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

function DataTable({ headers, rows, emptyMsg = 'No data for this period' }) {
  if (!rows?.length) return <Card className="border-[#E5E7E1]"><CardContent className="py-12 text-center text-sm text-[#5C6656]">{emptyMsg}</CardContent></Card>;
  return (
    <Card className="border-[#E5E7E1] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F9F8F6]"><tr className="border-b border-[#E5E7E1]">
            {headers.map((h, i) => <th key={i} className={`px-4 py-3 text-xs font-bold uppercase text-[#5C6656] ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>)}
          </tr></thead>
          <tbody>{rows.map((row, ri) => (
            <tr key={ri} className="border-b border-[#E5E7E1] last:border-0 hover:bg-[#F9F8F6]">
              {row.map((cell, ci) => <td key={ci} className={`px-4 py-3 ${ci === 0 ? 'text-left font-medium text-[#1A2421]' : 'text-right text-[#5C6656]'}`}>{cell}</td>)}
            </tr>
          ))}</tbody>
        </table>
      </div>
    </Card>
  );
}

export default function ReportsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const licence = useLicence();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const canReset = !!(
    licence && (
      licence.licence_tier === 'premium' ||
      (licence.licence_status === 'trial' && !licence.trial_expired) ||
      licence.tier_features?.data_reset === true
    )
  );
  const [view, setView] = useState('dashboard'); // 'dashboard' | 'detail'
  const [dashTab, setDashTab] = useState('performance');
  const [activeReport, setActiveReport] = useState(null);
  const [catalogCategory, setCatalogCategory] = useState('all');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [period, setPeriod] = useState('30');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [data, setData] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const getDateRange = useCallback(() => {
    const today = new Date();
    let end = format(today, 'yyyy-MM-dd');
    let start;
    if (period === 'day') start = end;
    else if (period === '7') start = format(subDays(today, 6), 'yyyy-MM-dd');
    else if (period === '14') start = format(subDays(today, 13), 'yyyy-MM-dd');
    else if (period === '30') start = format(subDays(today, 29), 'yyyy-MM-dd');
    else if (period === 'week') start = format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
    else if (period === 'month') start = format(startOfMonth(today), 'yyyy-MM-dd');
    else if (period === 'custom' && customStart && customEnd) { start = customStart; end = customEnd; }
    else start = format(subDays(today, 29), 'yyyy-MM-dd');
    return { start, end };
  }, [period, customStart, customEnd]);

  // Fetch main overview for dashboard tabs
  const fetchOverview = useCallback(async () => {
    setLoading(true);
    const { start, end } = getDateRange();
    try {
      const { data: d } = await axios.get(`${API}/reports/overview`, { params: { start, end }, withCredentials: true });
      setData(d);
    } catch (err) { if (err.response?.status === 403) toast.error('Upgrade required for reports'); }
    setLoading(false);
  }, [getDateRange]);

  useEffect(() => {
    if (view === 'dashboard') fetchOverview();
  }, [fetchOverview, view]);

  // Fetch detail data for catalog reports
  const fetchDetailReport = useCallback(async (report) => {
    setDetailLoading(true);
    const { start, end } = getDateRange();
    try {
      if (report.endpoint === 'overview') {
        const { data: d } = await axios.get(`${API}/reports/overview`, { params: { start, end }, withCredentials: true });
        setDetailData({ type: 'overview', ...d });
      } else if (report.endpoint === 'detail') {
        const detailType = report.key === 'discount_summary' ? 'discount_list' : report.key === 'tips_summary' ? 'tips_detail' : report.key;
        const { data: d } = await axios.get(`${API}/reports/detail`, { params: { type: detailType, start, end }, withCredentials: true });
        setDetailData({ type: 'detail', reportKey: report.key, ...d });
      } else if (report.endpoint === 'commissions') {
        const { data: d } = await axios.get(`${API}/staff/commissions/report`, { params: { start, end }, withCredentials: true });
        setDetailData({ type: 'commissions', ...d });
      }
    } catch { toast.error('Failed to load report'); }
    setDetailLoading(false);
  }, [getDateRange]);

  useEffect(() => {
    if (activeReport) fetchDetailReport(activeReport);
  }, [activeReport, fetchDetailReport]);

  const openReport = (report) => { setActiveReport(report); setView('detail'); setDetailData(null); };
  const goBack = () => { setView('dashboard'); setActiveReport(null); setDetailData(null); };

  const exportCSV = async () => {
    setExporting(true);
    const { start, end } = getDateRange();
    try {
      const res = await axios.get(`${API}/reports/accountant-export`, { params: { start, end, format: 'csv' }, withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a'); a.href = url; a.download = `apptio-report-${start}-to-${end}.csv`; a.click(); URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch { toast.error('Export failed'); }
    setExporting(false);
  };

  const exportPDF = async () => {
    setExporting(true);
    const { start, end } = getDateRange();
    try {
      const { data: d } = await axios.get(`${API}/reports/accountant-export`, { params: { start, end, format: 'json' }, withCredentials: true });
      const invs = d.invoices || [];
      const rows = invs.map(inv => ({
        Date: inv.date, Invoice: inv.invoice_number, Client: inv.client_name, Service: inv.service_name,
        Staff: inv.staff_name, Subtotal: money2((inv.subtotal || inv.final_total || 0)), Discount: money2((inv.discount || 0)),
        Tip: money2((inv.tip || 0)), Total: money2((inv.final_total || 0)), Method: inv.payment_method || '-', Status: inv.status
      }));
      if (!rows.length) { toast.error('No invoices in this period'); setExporting(false); return; }
      // Build payment-method summary — cash / card / bank_transfer / other + refunds (voided)
      const paid = invs.filter(i => i.status === 'paid');
      const voided = invs.filter(i => i.status === 'voided');
      const methodTotals = {};
      const methodCounts = {};
      for (const inv of paid) {
        const sp = inv.split_payments || [];
        if ((inv.payment_method || '') === 'split' && sp.length) {
          for (const s of sp) {
            const m = (s.method || 'cash');
            methodTotals[m] = (methodTotals[m] || 0) + (s.amount || 0);
            methodCounts[m] = (methodCounts[m] || 0) + 1;
          }
        } else {
          const m = (inv.payment_method || 'cash');
          methodTotals[m] = (methodTotals[m] || 0) + (inv.final_total || 0);
          methodCounts[m] = (methodCounts[m] || 0) + 1;
        }
      }
      // Refunds map — use voided invoices' payment method
      const refundTotals = {};
      const refundCounts = {};
      for (const inv of voided) {
        const sp = inv.split_payments || [];
        if ((inv.payment_method || '') === 'split' && sp.length) {
          for (const s of sp) {
            const m = (s.method || 'cash');
            refundTotals[m] = (refundTotals[m] || 0) + (s.amount || 0);
            refundCounts[m] = (refundCounts[m] || 0) + 1;
          }
        } else {
          const m = (inv.payment_method || 'cash');
          refundTotals[m] = (refundTotals[m] || 0) + (inv.final_total || 0);
          refundCounts[m] = (refundCounts[m] || 0) + 1;
        }
      }
      const allMethods = Array.from(new Set([...Object.keys(methodTotals), ...Object.keys(refundTotals)])).sort();
      const pretty = (m) => ({ cash: 'Cash', card: 'Card', bank_transfer: 'Bank transfer', split: 'Split', apple_pay: 'Apple Pay', google_pay: 'Google Pay' }[m] || (m || 'Other').replace(/_/g, ' '));
      const summaryRows = allMethods.map(m => ({
        Method: pretty(m),
        Payments: String(methodCounts[m] || 0),
        Amount: money2((methodTotals[m] || 0)),
        Refunds: String(refundCounts[m] || 0),
        RefundAmount: money2((refundTotals[m] || 0)),
        Net: money2(((methodTotals[m] || 0) - (refundTotals[m] || 0))),
      }));
      const totalPay = Object.values(methodTotals).reduce((a, b) => a + b, 0);
      const totalRef = Object.values(refundTotals).reduce((a, b) => a + b, 0);
      const totalCnt = Object.values(methodCounts).reduce((a, b) => a + b, 0);
      const totalRefCnt = Object.values(refundCounts).reduce((a, b) => a + b, 0);
      // VAT summary section (only if business is VAT registered)
      let vatSummary = null;
      if (d.business?.vat_enabled && (d.business.vat_rate || 0) > 0) {
        const rate = Number(d.business.vat_rate) || 20;
        const grossPaid = paid.reduce((s, i) => s + (i.final_total || 0), 0);
        const grossVoided = voided.reduce((s, i) => s + (i.final_total || 0), 0);
        const netPaid = grossPaid / (1 + rate / 100);
        const vatPaid = grossPaid - netPaid;
        const netVoided = grossVoided / (1 + rate / 100);
        const vatVoided = grossVoided - netVoided;
        vatSummary = {
          title: `VAT (${rate}%) — ${d.business.vat_number || 'Not provided'}`,
          headers: ['', 'Gross (Inc. VAT)', 'Net (Excl. VAT)', `VAT @ ${rate}%`],
          rows: [
            ['Sales (paid)', money2(grossPaid), money2(netPaid), money2(vatPaid)],
            ['Refunds (voided)', money2(grossVoided), money2(netVoided), money2(vatVoided)],
          ],
          total: ['Net VAT due', money2((grossPaid - grossVoided)), money2((netPaid - netVoided)), money2((vatPaid - vatVoided))],
        };
      }
      const res = await axios.post(`${API}/reports/export-pdf`, {
        title: `Accountant Report ${start} to ${end}`,
        rows,
        summary: {
          title: 'Payments by method',
          headers: ['Method', 'Payments', 'Amount', 'Refunds', 'Refund amount', 'Net'],
          rows: summaryRows.map(r => [r.Method, r.Payments, r.Amount, r.Refunds, r.RefundAmount, r.Net]),
          total: ['Total', String(totalCnt), money2(totalPay), String(totalRefCnt), money2(totalRef), money2((totalPay - totalRef))],
        },
        vatSummary,
      }, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `apptio-report-${start}-to-${end}.pdf`; a.click(); URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('Export failed'); }
    setExporting(false);
  };

  const localized = (r) => ({
    name: t(`reports.catalog.${r.key}.name`, r.name),
    desc: t(`reports.catalog.${r.key}.desc`, r.desc),
  });

  const filteredCatalog = REPORT_CATALOG.filter(r => {
    if (catalogCategory !== 'all' && r.category !== catalogCategory) return false;
    const loc = localized(r);
    const s = catalogSearch.toLowerCase();
    if (catalogSearch && !loc.name.toLowerCase().includes(s) && !loc.desc.toLowerCase().includes(s) && !r.name.toLowerCase().includes(s) && !r.desc.toLowerCase().includes(s)) return false;
    return true;
  });

  // ─── DETAIL VIEW (for catalog drill-downs) ───
  if (view === 'detail' && activeReport) {
    return (
      <div className="fade-in space-y-5" data-testid="report-detail-page">
        <div className="flex items-center gap-3">
          <button onClick={goBack} className="p-2 rounded-lg hover:bg-[#F5F7F0] transition-colors" data-testid="report-back-btn"><ArrowLeft size={20} className="text-[#5C6656]" /></button>
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-medium text-[#1A2421] tracking-tight" style={{ fontFamily: 'Outfit' }}>{t(`reports.catalog.${activeReport.key}.name`, activeReport.name)}</h1>
            <p className="text-xs text-[#5C6656] mt-0.5">{activeReport.desc}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={period} onChange={e => setPeriod(e.target.value)} className="h-9 border border-[#E5E7E1] rounded-lg px-3 text-sm bg-white text-[#1A2421]" data-testid="detail-period-select">
            {PERIODS.map(p => <option key={p.value} value={p.value}>{t(p.i18n, p.label)}</option>)}
          </select>
          {period === 'custom' && (<><input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="h-9 border border-[#E5E7E1] rounded-lg px-2 text-sm" /><input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="h-9 border border-[#E5E7E1] rounded-lg px-2 text-sm" /></>)}
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={exportCSV} disabled={exporting} className="border-[#556B2F] text-[#556B2F] h-9 text-xs gap-1"><Download size={13} /> CSV</Button>
            <Button variant="outline" onClick={exportPDF} disabled={exporting} className="border-[#E5E7E1] text-[#5C6656] h-9 text-xs gap-1"><Download size={13} /> PDF</Button>
          </div>
        </div>
        {detailLoading ? <div className="text-center py-16 text-[#5C6656]">Loading report...</div> : detailData ? <DetailReportContent reportKey={activeReport.key} data={detailData} /> : <div className="text-center py-16 text-[#5C6656]">No data</div>}
      </div>
    );
  }

  // ─── MAIN DASHBOARD VIEW ───
  const r = data;

  return (
    <div className="fade-in space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-medium text-[#1A2421] tracking-tight" style={{ fontFamily: 'Outfit' }}>{t('reports.title', 'Reports')}</h1>
          <p className="text-sm text-[#5C6656] mt-1">{t('reports.subtitle', 'Business analytics, exports and insights')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={period} onChange={e => setPeriod(e.target.value)} className="h-9 border border-[#E5E7E1] rounded-lg px-3 text-sm bg-white text-[#1A2421]" data-testid="period-select">
            {PERIODS.map(p => <option key={p.value} value={p.value}>{t(p.i18n, p.label)}</option>)}
          </select>
          {period === 'custom' && (<><input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="h-9 border border-[#E5E7E1] rounded-lg px-2 text-sm" /><input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="h-9 border border-[#E5E7E1] rounded-lg px-2 text-sm" /></>)}
          <Button variant="outline" onClick={exportCSV} disabled={exporting} className="border-[#556B2F] text-[#556B2F] h-9 text-xs gap-1" data-testid="export-csv"><Download size={13} /> CSV</Button>
          <Button variant="outline" onClick={exportPDF} disabled={exporting} className="border-[#E5E7E1] text-[#5C6656] h-9 text-xs gap-1" data-testid="export-pdf"><Download size={13} /> PDF</Button>
        </div>
      </div>

      {/* Dashboard Tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {DASH_TABS.filter(tb => {
          if (tb.adminOnly && !isAdmin) return false;
          if (tb.key === 'reset' && !canReset) return false;
          return true;
        }).map(tb => { const Icon = tb.icon; return (
          <button key={tb.key} onClick={() => setDashTab(tb.key)} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs whitespace-nowrap transition-colors ${dashTab === tb.key ? (tb.key === 'reset' ? 'border-[#E2725B] bg-[#E2725B]/10 text-[#E2725B] font-medium' : 'border-[#556B2F] bg-[#F5F7F0] text-[#556B2F] font-medium') : 'border-[#E5E7E1] text-[#5C6656] hover:border-[#556B2F]/50'}`} data-testid={`report-tab-${tb.key}`}>
            <Icon size={13} /> {t(tb.i18n, tb.label)}
            <Badge className={`text-[7px] px-1 py-0 ${tb.tier === 'premium' ? 'bg-[#8E44AD]/10 text-[#8E44AD]' : tb.tier === 'business' ? 'bg-[#556B2F]/10 text-[#556B2F]' : 'bg-[#E5E7E1] text-[#5C6656]'}`}>{tb.tier}</Badge>
          </button>
        ); })}
      </div>

      {/* Dashboard Content */}
      {dashTab === 'reset' && isAdmin && canReset ? (
        <ResetPanel />
      ) : loading ? <div className="text-center py-16 text-[#5C6656]">Loading...</div> : !r ? (
        <Card className="border-[#E5E7E1]"><CardContent className="py-16 text-center"><Lock size={40} className="mx-auto mb-3 text-[#E5E7E1]" /><p className="text-sm font-medium text-[#1A2421]">Reports require Business tier or higher</p></CardContent></Card>
      ) : (<>
        {/* ── Performance ── */}
        {dashTab === 'performance' && (<div className="space-y-6" data-testid="performance-dashboard">
          {r.voided_count > 0 && (<div className="bg-[#E2725B]/10 border border-[#E2725B]/20 rounded-lg px-4 py-2 flex items-center gap-2"><Ban size={14} className="text-[#E2725B]" /><span className="text-sm text-[#E2725B]">{r.voided_count} voided invoice{r.voided_count > 1 ? 's' : ''} excluded</span></div>)}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <MetricCard icon={DollarSign} label={t('reports.totalSales')} value={money2(r.total_sales.value)} change={r.total_sales.change} sub={t('reports.vsCompPeriod')} color="#556B2F" />
            <MetricCard icon={ShoppingBag} label={t('reports.avgSale')} value={money2(r.avg_sale.value)} change={r.avg_sale.change} sub={t('reports.vsCompPeriod')} color="#3E6641" />
            <MetricCard icon={Calendar} label={t('reports.appointments')} value={r.appointments.value} change={r.appointments.change} sub={t('reports.completedCount', { count: r.appointments.completed })} color="#2E86C1" />
            <MetricCard icon={UserCheck} label={t('reports.returningRate')} value={`${r.returning_rate.value}%`} change={r.returning_rate.change} color="#D4A373" />
            <MetricCard icon={TrendingUp} label={t('reports.onlineSales')} value={money2(r.online_sales)} sub={`${t('reports.offlinePrefix')}: ${money2(r.offline_sales)}`} color="#8E44AD" />
          </div>
          <Card className="border-[#E5E7E1]"><CardHeader className="pb-1"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>{t('reports.salesOverTime')}</CardTitle></CardHeader><CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={r.daily_chart}><CartesianGrid strokeDasharray="3 3" stroke="#E5E7E1" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#5C6656' }} tickFormatter={d => { try { return format(new Date(d + 'T12:00'), 'd MMM'); } catch { return d; } }} />
                <YAxis tick={{ fontSize: 10, fill: '#5C6656' }} tickFormatter={v => money(v)} /><Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #E5E7E1', fontSize: 12 }} formatter={v => [money2(Number(v)), 'Revenue']} />
                <Bar dataKey="revenue" fill="#556B2F" radius={[4, 4, 0, 0]} /></BarChart>
            </ResponsiveContainer>
          </CardContent></Card>
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="border-[#E5E7E1]"><CardHeader className="pb-2"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>{t('reports.appointments')}</CardTitle></CardHeader><CardContent>
              <p className="text-3xl font-bold text-[#1A2421]">{r.appointments.value}</p><Change value={r.appointments.change} />
              <div className="mt-4 space-y-2">{[{ l: t('reports.completed'), v: r.appointments.completed, c: '#3E6641' }, { l: t('reports.clientCancellations'), v: r.appointments.client_cancellations || 0, c: '#E2725B' }, { l: t('reports.cancelledAppts'), v: r.appointments.cancelled, c: '#E2725B' }, { l: t('reports.noShows'), v: r.appointments.no_show, c: '#C0392B' }].map(s => (<div key={s.l} className="flex justify-between"><span className="text-sm text-[#5C6656]">{s.l}</span><span className="text-sm font-bold" style={{ color: s.c }}>{s.v}</span></div>))}</div>
            </CardContent></Card>
            <Card className="border-[#E5E7E1]"><CardHeader className="pb-2"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>{t('reports.clients')}</CardTitle></CardHeader><CardContent>
              <div className="space-y-3">{[{ l: t('reports.returning'), v: r.clients.returning, c: '#556B2F' }, { l: t('reports.new'), v: r.clients.new, c: '#3E6641' }, { l: t('reports.walkIns'), v: r.clients.walkin, c: '#D4A373' }].map(s => { const total = Math.max(r.clients.returning + r.clients.new + r.clients.walkin, 1); return (<div key={s.l} className="flex items-center gap-3"><span className="text-sm text-[#5C6656] w-24">{s.l}</span><div className="flex-1 h-5 bg-[#F5F7F0] rounded overflow-hidden"><div className="h-full rounded" style={{ width: `${(s.v / total) * 100}%`, backgroundColor: s.c }} /></div><span className="text-sm font-bold text-[#1A2421] w-10 text-right">{s.v}</span></div>); })}</div>
            </CardContent></Card>
          </div>
        </div>)}

        {/* ── Top Treatments ── */}
        {dashTab === 'treatments' && (<div className="space-y-4" data-testid="treatments-report">
          <Card className="border-[#E5E7E1]"><CardHeader><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Top Performing Treatments</CardTitle></CardHeader><CardContent className="p-0">
            <table className="w-full text-sm"><thead className="bg-[#F9F8F6]"><tr className="border-b border-[#E5E7E1]"><th className="text-left px-5 py-3 text-xs font-bold uppercase text-[#5C6656]">#</th><th className="text-left px-3 py-3 text-xs font-bold uppercase text-[#5C6656]">Service</th><th className="text-right px-3 py-3 text-xs font-bold uppercase text-[#5C6656]">Sales</th><th className="text-right px-3 py-3 text-xs font-bold uppercase text-[#5C6656]">Revenue</th><th className="text-right px-5 py-3 text-xs font-bold uppercase text-[#5C6656]">Avg</th></tr></thead>
            <tbody>{(r.top_services || []).map((s, i) => (<tr key={i} className="border-b border-[#E5E7E1] last:border-0 hover:bg-[#F9F8F6]"><td className="px-5 py-3 text-[#5C6656] font-bold">{i + 1}</td><td className="px-3 py-3 font-medium text-[#1A2421]">{s.service}</td><td className="px-3 py-3 text-right text-[#5C6656]">{s.count}</td><td className="px-3 py-3 text-right font-bold text-[#556B2F]">{money2(s.revenue)}</td><td className="px-5 py-3 text-right text-[#5C6656]">{money2(s.avg)}</td></tr>))}</tbody></table>
          </CardContent></Card>
          {(r.top_services || []).length > 0 && (<Card className="border-[#E5E7E1]"><CardContent className="p-5"><ResponsiveContainer width="100%" height={300}><BarChart data={(r.top_services || []).slice(0, 10)} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#E5E7E1" /><XAxis type="number" tick={{ fontSize: 10, fill: '#5C6656' }} tickFormatter={v => money(v)} /><YAxis dataKey="service" type="category" width={150} tick={{ fontSize: 10, fill: '#5C6656' }} /><Tooltip formatter={v => [money2(v), 'Revenue']} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7E1', fontSize: 12 }} /><Bar dataKey="revenue" fill="#556B2F" radius={[0, 4, 4, 0]} /></BarChart></ResponsiveContainer></CardContent></Card>)}
        </div>)}

        {/* ── Team ── */}
        {dashTab === 'staff' && (<Card className="border-[#E5E7E1]" data-testid="staff-report"><CardHeader><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Team Performance</CardTitle></CardHeader><CardContent className="p-0">
          <table className="w-full text-sm"><thead className="bg-[#F9F8F6]"><tr className="border-b border-[#E5E7E1]"><th className="text-left px-5 py-3 text-xs font-bold uppercase text-[#5C6656]">Team Member</th><th className="text-right px-3 py-3 text-xs font-bold uppercase text-[#5C6656]">Sales</th><th className="text-right px-3 py-3 text-xs font-bold uppercase text-[#5C6656]">Revenue</th><th className="text-right px-5 py-3 text-xs font-bold uppercase text-[#5C6656]">Clients</th></tr></thead>
          <tbody>{(r.staff || []).map((s, i) => (<tr key={i} className="border-b border-[#E5E7E1] last:border-0 hover:bg-[#F9F8F6]"><td className="px-5 py-3 font-medium text-[#1A2421] flex items-center gap-2"><div className="w-7 h-7 rounded-full bg-[#E9EDDF] flex items-center justify-center text-[10px] font-bold text-[#556B2F]">{s.staff?.charAt(0)?.toUpperCase()}</div>{s.staff}</td><td className="px-3 py-3 text-right text-[#5C6656]">{s.sales}</td><td className="px-3 py-3 text-right font-bold text-[#556B2F]">{money2(s.revenue)}</td><td className="px-5 py-3 text-right text-[#5C6656]">{s.clients}</td></tr>))}</tbody></table>
        </CardContent></Card>)}

        {/* ── Payments ── */}
        {dashTab === 'payments' && (<div className="space-y-4" data-testid="payments-report">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{(r.payment_breakdown || []).map(p => (<Card key={p.method} className="border-[#E5E7E1]"><CardContent className="p-4 text-center"><p className="text-[10px] font-bold uppercase tracking-wider text-[#5C6656] capitalize">{p.method === 'bank_transfer' ? 'Transfer' : p.method}</p><p className="text-2xl font-bold text-[#1A2421] mt-1" style={{ fontFamily: 'Outfit' }}>{money2(p.amount)}</p></CardContent></Card>))}</div>
          <Card className="border-[#E5E7E1]"><CardContent className="p-5">
            <div className="space-y-3">
              <div className="flex justify-between text-sm"><span className="text-[#5C6656]">Revenue</span><span className="font-bold">{money2(r.total_sales.value)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[#5C6656]">Tips</span><span className="font-bold text-[#3E6641]">{money2(r.tips)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-[#5C6656]">Discounts</span><span className="font-bold text-[#E2725B]">-{money2(r.discounts)}</span></div>
              <div className="border-t pt-2 flex justify-between"><span className="font-bold">Net</span><span className="font-bold text-[#556B2F] text-lg">{money2((r.total_sales.value - r.discounts + r.tips))}</span></div>
            </div>
          </CardContent></Card>
        </div>)}

        {/* ── Clients ── */}
        {dashTab === 'clients' && (<div className="space-y-4" data-testid="clients-report">
          <div className="grid grid-cols-3 gap-4"><MetricCard icon={UserCheck} label={t('reports.returning')} value={r.clients.returning} color="#556B2F" /><MetricCard icon={Users} label={t('reports.new')} value={r.clients.new} color="#3E6641" /><MetricCard icon={Users} label={t('reports.walkIns')} value={r.clients.walkin} color="#D4A373" /></div>
          <MetricCard icon={TrendingUp} label={t('reports.returningRate')} value={`${r.returning_rate.value}%`} change={r.returning_rate.change} sub={t('reports.vsComparison')} color="#556B2F" />
        </div>)}

        {/* ── Accountant ── */}
        {dashTab === 'accountant' && (<Card className="border-[#E5E7E1]" data-testid="accountant-report"><CardContent className="py-12 text-center space-y-4">
          <FileText size={40} className="mx-auto text-[#556B2F]" />
          <div><p className="text-lg font-bold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('reports.accountantExport')}</p><p className="text-sm text-[#5C6656] mt-1">{t('reports.accountantExportDesc')}</p></div>
          <div className="flex justify-center gap-3 pt-2"><Button onClick={exportCSV} disabled={exporting} className="bg-[#556B2F] text-white gap-1.5"><Download size={14} /> {t('reports.downloadCsv')}</Button><Button onClick={exportPDF} disabled={exporting} variant="outline" className="border-[#E5E7E1] gap-1.5"><Download size={14} /> {t('reports.downloadPdf')}</Button></div>
          <p className="text-xs text-[#5C6656]">{t('reports.accountantExportIncludes')}</p>
        </CardContent></Card>)}
      </>)}

      {/* ═══ REPORT CATALOG (Fresha-style) ═══ */}
      <div className="border-t border-[#E5E7E1] pt-8 mt-8" data-testid="report-catalog-section">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('reports.allReports')} <span className="text-sm font-normal text-[#5C6656] ml-1">{REPORT_CATALOG.length}</span></h2>
            <p className="text-xs text-[#5C6656] mt-0.5">{t('reports.detailedBreakdowns')}</p>
          </div>
          <div className="relative w-64">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B0B5A8]" />
            <input value={catalogSearch} onChange={e => setCatalogSearch(e.target.value)} placeholder={t('reports.searchReports')}
              className="w-full h-8 border border-[#E5E7E1] rounded-lg pl-9 pr-3 text-xs bg-white text-[#1A2421] placeholder:text-[#B0B5A8] focus:outline-none focus:ring-2 focus:ring-[#556B2F]/20"
              data-testid="report-search" />
          </div>
        </div>

        <div className="flex gap-1 overflow-x-auto pb-2 mb-3">
          {CATALOG_CATEGORIES.map(cat => (
            <button key={cat.key} onClick={() => setCatalogCategory(cat.key)}
              className={`px-3 py-1.5 rounded-md text-xs whitespace-nowrap transition-colors ${catalogCategory === cat.key ? 'bg-[#1A2421] text-white font-medium' : 'text-[#5C6656] hover:bg-[#F5F7F0] border border-[#E5E7E1]'}`}
              data-testid={`cat-tab-${cat.key}`}>
              {t(cat.i18n, cat.label)}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
          {filteredCatalog.map(report => {
            const Icon = ICON_MAP[report.icon] || FileText;
            return (
              <Card key={report.key} className="border-[#E5E7E1] hover:border-[#556B2F]/40 hover:shadow-sm transition-all cursor-pointer group"
                onClick={() => openReport(report)} data-testid={`report-card-${report.key}`}>
                <CardContent className="p-3.5 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#F5F7F0] flex items-center justify-center flex-shrink-0 group-hover:bg-[#E9EDDF] transition-colors">
                    <Icon size={16} className="text-[#556B2F]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1A2421] leading-tight">{t(`reports.catalog.${report.key}.name`, report.name)}</p>
                    <p className="text-[11px] text-[#5C6656] mt-0.5 truncate">{t(`reports.catalog.${report.key}.desc`, report.desc)}</p>
                  </div>
                  <ChevronRight size={14} className="text-[#B0B5A8] flex-shrink-0 group-hover:text-[#556B2F] transition-colors" />
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Detail Report Content (for catalog drill-downs) ───
function DetailReportContent({ reportKey, data: r }) {
  if (!r) return null;

  if (reportKey === 'sales_list' && r.rows) {
    return (<div className="space-y-3"><div className="text-sm text-[#5C6656]">{r.count} transaction{r.count !== 1 ? 's' : ''}</div>
      <DataTable headers={['Date', 'Invoice', 'Client', 'Service', 'Staff', 'Total', 'Method', 'Status']}
        rows={r.rows.map(inv => [inv.date, inv.invoice_number || '-', inv.client_name || '-', inv.service_name || '-', inv.staff_name || '-', money2((inv.final_total || 0)), (inv.payment_method || 'cash').replace('bank_transfer', 'Transfer'), inv.status])} /></div>);
  }

  if (reportKey === 'discount_summary' && r.type === 'detail') {
    return (<div className="space-y-5">
      <div className="grid grid-cols-3 gap-3">
        <MetricCard icon={Percent} label="Total Discounts" value={money2((r.total_discount || 0))} color="#E2725B" />
        <MetricCard icon={DollarSign} label="Original Value" value={money2((r.total_original || 0))} color="#556B2F" />
        <MetricCard icon={Receipt} label="Discounted Sales" value={r.count || 0} color="#2E86C1" />
      </div>
      <DataTable headers={['Date', 'Invoice', 'Client', 'Service', 'Original', 'Discount', 'Final']}
        rows={(r.rows || []).map(inv => [inv.date, inv.invoice_number || '-', inv.client_name || '-', inv.service_name || '-', money2(((inv.final_total || 0) + (inv.discount || 0))), `-${money2((inv.discount || 0))}`, money2((inv.final_total || 0))])} /></div>);
  }

  if (reportKey === 'finance_summary' && r.type === 'overview') {
    return (<div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <MetricCard icon={DollarSign} label="Revenue" value={money2(r.total_sales.value)} change={r.total_sales.change} color="#556B2F" />
        <MetricCard icon={Heart} label="Tips" value={money2(r.tips)} color="#3E6641" />
        <MetricCard icon={Percent} label="Discounts" value={`-${money2(r.discounts)}`} color="#E2725B" />
        <MetricCard icon={TrendingUp} label="Net Revenue" value={money2((r.total_sales.value - r.discounts + r.tips))} color="#2E86C1" />
        <MetricCard icon={Ban} label="Voided" value={r.voided_count} color="#C0392B" />
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="border-[#E5E7E1]"><CardHeader className="pb-1"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Payment Methods</CardTitle></CardHeader><CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart><Pie data={(r.payment_breakdown || []).filter(p => p.amount > 0)} dataKey="amount" nameKey="method" cx="50%" cy="50%" outerRadius={80} label={({ method, amount }) => `${method}: ${money(amount)}`}>
              {(r.payment_breakdown || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}</Pie><Tooltip formatter={v => [money2(Number(v))]} /></PieChart>
          </ResponsiveContainer></CardContent></Card>
        <Card className="border-[#E5E7E1]"><CardHeader className="pb-1"><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Revenue Over Time</CardTitle></CardHeader><CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={r.daily_chart}><CartesianGrid strokeDasharray="3 3" stroke="#E5E7E1" />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#5C6656' }} tickFormatter={d => { try { return format(new Date(d + 'T12:00'), 'd MMM'); } catch { return d; } }} />
              <YAxis tick={{ fontSize: 10, fill: '#5C6656' }} tickFormatter={v => money(v)} /><Tooltip formatter={v => [money2(Number(v))]} />
              <Bar dataKey="revenue" fill="#556B2F" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer></CardContent></Card>
      </div></div>);
  }

  if (reportKey === 'payment_transactions' && r.rows) {
    return (<div className="space-y-3"><div className="text-sm text-[#5C6656]">{r.count} transaction{r.count !== 1 ? 's' : ''}</div>
      <DataTable headers={['Date', 'Invoice', 'Client', 'Amount', 'Method', 'Tip', 'Status']}
        rows={r.rows.map(inv => [inv.date, inv.invoice_number || '-', inv.client_name || '-', money2((inv.final_total || 0)), (inv.payment_method || 'cash').replace('bank_transfer', 'Transfer'), inv.tip ? money2(inv.tip) : '-', inv.status])} /></div>);
  }

  if (reportKey === 'tips_summary' && r.type === 'detail') {
    return (<div className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        <MetricCard icon={Heart} label="Total Tips" value={money2((r.total_tips || 0))} color="#3E6641" />
        <MetricCard icon={Receipt} label="Tipped Sales" value={r.count || 0} color="#556B2F" />
      </div>
      {(r.by_staff || []).length > 0 && (<Card className="border-[#E5E7E1]"><CardHeader><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Tips by Team Member</CardTitle></CardHeader><CardContent>
        <ResponsiveContainer width="100%" height={Math.max(150, (r.by_staff || []).length * 40)}>
          <BarChart data={r.by_staff} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#E5E7E1" />
            <XAxis type="number" tick={{ fontSize: 10, fill: '#5C6656' }} tickFormatter={v => money(v)} /><YAxis dataKey="staff" type="category" width={120} tick={{ fontSize: 11, fill: '#5C6656' }} />
            <Tooltip formatter={v => [money2(Number(v)), 'Tips']} /><Bar dataKey="tips" fill="#3E6641" radius={[0, 4, 4, 0]} /></BarChart>
        </ResponsiveContainer></CardContent></Card>)}
      <DataTable headers={['Date', 'Client', 'Staff', 'Sale Total', 'Tip']}
        rows={(r.rows || []).map(inv => [inv.date, inv.client_name || '-', inv.staff_name || '-', money2((inv.final_total || 0)), money2((inv.tip || 0))])} /></div>);
  }

  if (reportKey === 'appointments_list' && r.rows) {
    const money0 = (n) => money2(n || 0);
    return (<div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard icon={Calendar} label="Total Appointment Value" value={money0(r.total_value)} sub={`${(r.confirmed_count || 0) + (r.completed_count || 0)} active`} color="#556B2F" />
        <MetricCard icon={Clock} label="Upcoming (Confirmed)" value={money0(r.confirmed_value)} sub={`${r.confirmed_count || 0} appt${(r.confirmed_count || 0) !== 1 ? 's' : ''}`} color="#2E86C1" />
        <MetricCard icon={CheckCircle} label="Completed Revenue" value={money0(r.completed_value)} sub={`${r.completed_count || 0} appt${(r.completed_count || 0) !== 1 ? 's' : ''}`} color="#3E6641" />
        <MetricCard icon={XCircle} label="Cancelled / No-show" value={(r.cancelled_count || 0) + (r.no_show_count || 0)} sub={`${money0(r.cancelled_value)} lost`} color="#E2725B" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard icon={DollarSign} label="Average Value" value={money0(r.avg_value)} sub="per appointment" color="#556B2F" />
        <MetricCard icon={Clock} label="Total Hours" value={`${r.total_hours || 0}h`} sub="booked time" color="#2E86C1" />
        <MetricCard icon={Calendar} label="Busiest Day" value={r.busiest_day?.day || '-'} sub={r.busiest_day ? `${r.busiest_day.count} appts · ${money0(r.busiest_day.value)}` : ''} color="#D4A373" />
        <MetricCard icon={TrendingUp} label="Online Bookings" value={`${r.online_count || 0}`} sub={`${money0(r.online_value)} online · ${money0(r.in_salon_value)} in-salon`} color="#3E6641" />
      </div>
      {(r.top_services || []).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border-[#E5E7E1]">
            <CardHeader><CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Scissors size={16} /> Top Services by Value</CardTitle></CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {r.top_services.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="truncate pr-2"><span className="text-[#1A2421] font-medium">{s.name}</span> <span className="text-[#5C6656] text-xs">· {s.count}</span></div>
                    <span className="font-semibold text-[#556B2F]">{money0(s.value)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E5E7E1]">
            <CardHeader><CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Outfit' }}><Users size={16} /> Top Staff by Value</CardTitle></CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {(r.top_staff || []).map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div><span className="text-[#1A2421] font-medium">{s.name}</span> <span className="text-[#5C6656] text-xs">· {s.count} appts</span></div>
                    <span className="font-semibold text-[#556B2F]">{money0(s.value)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      {(r.by_day || []).length > 0 && (
        <Card className="border-[#E5E7E1]">
          <CardHeader><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Appointments by Day of Week</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={r.by_day}><CartesianGrid strokeDasharray="3 3" stroke="#E5E7E1" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#5C6656' }} /><YAxis tick={{ fontSize: 10, fill: '#5C6656' }} />
                <Tooltip formatter={(v, n) => n === 'value' ? [money2(Number(v)), 'Value'] : [v, 'Appointments']} /><Bar dataKey="count" fill="#556B2F" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
      <div className="text-sm text-[#5C6656]">{r.count} appointment{r.count !== 1 ? 's' : ''} in range</div>
      <DataTable headers={['Date', 'Time', 'Client', 'Service', 'Staff', 'Price', 'Status']}
        rows={r.rows.map(a => [a.date, `${a.start_time}-${a.end_time}`, a.client_name || '-', a.service_name || '-', a.staff_name || '-', money2((a.price || 0)), a.status])} /></div>);
  }

  if (reportKey === 'cancellations_noshow' && r.type === 'detail') {
    return (<div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <MetricCard icon={Calendar} label="Total Appointments" value={r.total_appointments} color="#2E86C1" />
        <MetricCard icon={Users} label="Client Cancellations" value={r.cancelled} sub="unique clients" color="#E2725B" />
        <MetricCard icon={XCircle} label="Cancelled Appts" value={r.cancelled_treatments || r.cancelled} sub={`${r.cancellation_rate}% rate`} color="#E2725B" />
        <MetricCard icon={Ban} label="No-shows" value={r.no_show} sub={`${r.no_show_rate}% rate`} color="#C0392B" />
        <MetricCard icon={TrendingUp} label="Attendance Rate" value={`${(100 - (r.cancellation_rate || 0) - (r.no_show_rate || 0)).toFixed(1)}%`} color="#3E6641" />
      </div>
      {(r.by_reason || []).length > 0 && (<Card className="border-[#E5E7E1]"><CardHeader><CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Cancellation Reasons</CardTitle></CardHeader><CardContent>
        <ResponsiveContainer width="100%" height={Math.max(150, (r.by_reason || []).length * 40)}>
          <BarChart data={r.by_reason} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#E5E7E1" />
            <XAxis type="number" tick={{ fontSize: 10, fill: '#5C6656' }} /><YAxis dataKey="reason" type="category" width={160} tick={{ fontSize: 11, fill: '#5C6656' }} />
            <Tooltip /><Bar dataKey="count" fill="#E2725B" radius={[0, 4, 4, 0]} /></BarChart>
        </ResponsiveContainer></CardContent></Card>)}
      <DataTable headers={['Date', 'Client', 'Services', 'Staff', 'Status', 'Reason']}
        rows={(r.rows || []).map(a => [a.date, a.client_name || '-', a._service_count > 1 ? `${a._services?.join(', ')} (${a._service_count})` : (a.service_name || '-'), a.staff_name || '-', a.status, a.cancel_reason || '-'])} /></div>);
  }

  if (reportKey === 'staff_commissions' && r.type === 'commissions') {
    return (<div className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        <MetricCard icon={DollarSign} label="Total Commission" value={money2((r.total_commission || 0))} color="#556B2F" />
        <MetricCard icon={Users} label="Team Members" value={(r.staff || []).length} color="#2E86C1" />
      </div>
      <DataTable headers={['Team Member', 'Rate', 'Appointments', 'Revenue', 'Commission']}
        rows={(r.staff || []).map(s => [s.name, s.commission_type === 'percentage' ? `${s.commission_rate}%` : money(s.commission_rate), s.total_appointments, money2(s.total_revenue), money2(s.commission_earned)])} /></div>);
  }

  if (reportKey === 'client_list' && r.rows) {
    return (<div className="space-y-3"><div className="text-sm text-[#5C6656]">{r.count} client{r.count !== 1 ? 's' : ''}</div>
      <DataTable headers={['Name', 'Email', 'Phone', 'Appointments', 'Total Spent']}
        rows={r.rows.map(c => [c.name || '-', c.email || '-', c.phone || '-', c.appointment_count || 0, money2((c.total_spent || 0))])} /></div>);
  }

  return <Card className="border-[#E5E7E1]"><CardContent className="py-12 text-center text-sm text-[#5C6656]">Report content loading...</CardContent></Card>;
}


// ─── Reset Panel (admin only) ───
function ResetPanel() {
  const { t } = useTranslation();
  const [scope, setScope] = useState('all');  // 'all' | 'cancellations'
  const [mode, setMode] = useState('day');    // 'day' | 'before'
  const [paymentFilter, setPaymentFilter] = useState('any'); // 'any' | 'cash_only'
  const [startDate, setStartDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [preview, setPreview] = useState(null);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isCancelScope = scope === 'cancellations' || scope === 'cancellations_manual';
  const effectivePayFilter = isCancelScope ? 'any' : paymentFilter;
  const canPreview = () => !!startDate;

  const loadPreview = useCallback(async () => {
    if (!startDate) { setPreview(null); return; }
    setLoadingPrev(true);
    try {
      const { data } = await axios.get(`${API}/reports/reset-preview`, { params: { mode, scope, start_date: startDate, payment_filter: effectivePayFilter }, withCredentials: true });
      setPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('reports.resetPreviewFailed'));
      setPreview(null);
    }
    setLoadingPrev(false);
  }, [scope, mode, startDate, effectivePayFilter, t]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const totalRecords = preview ? (preview.appointments + preview.invoices + preview.payments + preview.activity) : 0;

  const submit = async () => {
    if (confirmText.trim().toUpperCase() !== 'RESET') { toast.error(t('reports.resetTypeRESETError')); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/reports/reset-data`, { mode, scope, start_date: startDate, payment_filter: effectivePayFilter }, { withCredentials: true });
      toast.success(t('reports.resetSuccess', { appts: data.moved.appointments, invoices: data.moved.invoices, payments: data.moved.payments }));
      setConfirmOpen(false);
      setConfirmText('');
      setPreview(null);
      setStartDate(format(new Date(), 'yyyy-MM-dd'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('reports.resetFailed'));
    }
    setSubmitting(false);
  };

  const dateLabel = mode === 'before' ? t('reports.resetCutoffDate') : t('reports.resetDay');

  return (
    <div className="space-y-5" data-testid="reset-panel">
      <Card className="border-[#E2725B]/30 bg-[#FFF8F5]">
        <CardContent className="p-5 flex gap-3">
          <AlertTriangle size={24} className="text-[#E2725B] flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('reports.resetClearHistoricalData')}</p>
            <p className="text-xs text-[#5C6656] leading-relaxed">{t('reports.resetDescription')} <strong>{t('reports.resetPermanent')}</strong></p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-[#E5E7E1]">
        <CardContent className="p-5 space-y-5">
          {/* Scope */}
          <div>
            <label className="text-xs font-medium text-[#5C6656] uppercase tracking-wide">{t('reports.resetWhatToReset')}</label>
            <div className="mt-2 flex gap-2 flex-wrap">
              {[
                { k: 'all', label: t('reports.resetAllData'), desc: t('reports.resetAllDataDesc') },
                { k: 'cancellations', label: t('reports.resetCancellationsOnly'), desc: t('reports.resetCancellationsOnlyDesc') },
                { k: 'cancellations_manual', label: 'Manual cancellations only', desc: 'Wipe only manually cancelled appointments — keep all online cancellation records intact' },
              ].map(opt => (
                <button key={opt.k} onClick={() => setScope(opt.k)} data-testid={`reset-scope-${opt.k}`}
                  className={`text-left px-3 py-2 rounded-lg border transition-colors ${scope === opt.k ? 'border-[#556B2F] bg-[#F5F7F0]' : 'border-[#E5E7E1] hover:border-[#556B2F]/50'}`}>
                  <p className={`text-sm font-medium ${scope === opt.k ? 'text-[#556B2F]' : 'text-[#1A2421]'}`}>{opt.label}</p>
                  <p className="text-[11px] text-[#5C6656]">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Payment method filter (only for 'all' scope) */}
          {scope === 'all' && (
            <div>
              <label className="text-xs font-medium text-[#5C6656] uppercase tracking-wide">{t('reports.paymentMethod')}</label>
              <div className="mt-2 flex gap-2 flex-wrap">
                {[
                  { k: 'any', label: t('reports.allPaymentMethods'), desc: t('reports.allPaymentMethodsDesc') },
                  { k: 'cash_only', label: t('reports.cashOnly'), desc: t('reports.cashOnlyDesc') },
                ].map(opt => (
                  <button key={opt.k} onClick={() => setPaymentFilter(opt.k)} data-testid={`reset-payfilter-${opt.k}`}
                    className={`text-left px-3 py-2 rounded-lg border transition-colors flex-1 min-w-[180px] ${paymentFilter === opt.k ? 'border-[#556B2F] bg-[#F5F7F0]' : 'border-[#E5E7E1] hover:border-[#556B2F]/50'}`}>
                    <p className={`text-sm font-medium ${paymentFilter === opt.k ? 'text-[#556B2F]' : 'text-[#1A2421]'}`}>{opt.label}</p>
                    <p className="text-[11px] text-[#5C6656]">{opt.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Mode */}
          <div>
            <label className="text-xs font-medium text-[#5C6656] uppercase tracking-wide">{t('reports.resetDateRange')}</label>
            <div className="mt-2 flex gap-2 flex-wrap">
              {[
                { k: 'day', label: t('reports.resetSingleDay'), hint: t('reports.resetSingleDayHint') },
                { k: 'before', label: t('reports.resetBeforeDate'), hint: t('reports.resetBeforeDateHint') },
              ].map(opt => (
                <button key={opt.k} onClick={() => setMode(opt.k)} data-testid={`reset-mode-${opt.k}`}
                  className={`text-left px-3 py-2 rounded-lg border transition-colors flex-1 min-w-[180px] ${mode === opt.k ? 'border-[#556B2F] bg-[#F5F7F0]' : 'border-[#E5E7E1] hover:border-[#556B2F]/50'}`}>
                  <p className={`text-sm font-medium ${mode === opt.k ? 'text-[#556B2F]' : 'text-[#1A2421]'}`}>{opt.label}</p>
                  <p className="text-[11px] text-[#5C6656]">{opt.hint}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Day picker */}
          <div className="flex items-center gap-3 flex-wrap">
            <div>
              <label className="text-[10px] text-[#5C6656] uppercase tracking-wide font-medium">{dateLabel}</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} max={format(new Date(), 'yyyy-MM-dd')}
                className="mt-1 block h-10 border border-[#E5E7E1] rounded-lg px-3 text-sm bg-white text-[#1A2421] focus:outline-none focus:ring-2 focus:ring-[#556B2F]/20" data-testid="reset-start-date" />
            </div>
            {startDate && (
              <p className="text-xs text-[#5C6656] mt-5">
                {mode === 'before'
                  ? <>{isCancelScope ? t('reports.resetAllCancellationsBefore') : t('reports.resetAllRecordsBefore')} <strong>{startDate}</strong> {t('reports.resetKeptRecordsOn', { date: startDate })}{scope === 'cancellations_manual' && ' Online cancellations are preserved.'}</>
                  : <>{t('reports.resetOnlyRecordsOn')} <strong>{startDate}</strong>{scope === 'cancellations' && t('reports.resetLimitedToCancellations')}{scope === 'cancellations_manual' && ' (manual cancellations only — online cancellations preserved)'}.</>}
              </p>
            )}
          </div>

          {canPreview() && (
            loadingPrev ? <div className="text-sm text-[#5C6656]">{t('reports.resetCalculating')}</div> : preview ? (
              <div className="border border-[#E5E7E1] rounded-lg p-4 bg-[#FAFAF8]" data-testid="reset-preview">
                <p className="text-xs font-semibold text-[#5C6656] uppercase tracking-wide mb-3">{t('reports.resetRecordsToRemove')}</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div><p className="text-2xl font-bold text-[#1A2421]">{preview.appointments}</p><p className="text-[11px] text-[#5C6656]">{t('reports.resetAppointments')}</p></div>
                  <div><p className="text-2xl font-bold text-[#1A2421]">{preview.invoices}</p><p className="text-[11px] text-[#5C6656]">{t('reports.resetInvoices')}</p></div>
                  <div><p className="text-2xl font-bold text-[#1A2421]">{preview.payments}</p><p className="text-[11px] text-[#5C6656]">{t('reports.resetPayments')}</p></div>
                  <div><p className="text-2xl font-bold text-[#1A2421]">{preview.activity}</p><p className="text-[11px] text-[#5C6656]">{t('reports.resetActivityLogs')}</p></div>
                </div>
              </div>
            ) : null
          )}

          <div className="flex justify-end pt-1">
            <Button onClick={() => setConfirmOpen(true)} disabled={!canPreview() || !preview || totalRecords === 0}
              className="bg-[#E2725B] hover:bg-[#C95A44] text-white gap-1.5" data-testid="reset-start-btn">
              <AlertTriangle size={14} /> {totalRecords > 0 ? t('reports.resetButton', { count: totalRecords }) : t('reports.resetData')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {confirmOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !submitting && setConfirmOpen(false)} data-testid="reset-confirm-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <div className="flex gap-3">
              <AlertTriangle size={28} className="text-[#E2725B] flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>{t('reports.resetAreYouSure')}</h3>
                <p className="text-sm text-[#5C6656] mt-1">
                  {scope === 'cancellations' ? t('reports.resetCancellationRecords') : scope === 'cancellations_manual' ? 'Manual cancellation records' : t('reports.resetAllDataPrefix')}
                  {mode === 'before' ? <>{t('reports.resetBeforeText')} <strong>{startDate}</strong></> : <>{t('reports.resetOnText')} <strong>{startDate}</strong></>}
                  {' '}{t('reports.resetSuffix')}
                  {scope === 'cancellations_manual' && <span className="block mt-1 text-[#3E6641]">Online cancellations will be preserved.</span>}
                </p>
              </div>
            </div>
            <div className="bg-[#FAFAF8] border border-[#E5E7E1] rounded-lg p-3 text-xs text-[#5C6656] space-y-1">
              <p>· {preview?.appointments || 0} {t('reports.resetAppointments').toLowerCase()}</p>
              <p>· {preview?.invoices || 0} {t('reports.resetInvoices').toLowerCase()}</p>
              <p>· {preview?.payments || 0} {t('reports.resetPayments').toLowerCase()}</p>
              <p>· {preview?.activity || 0} {t('reports.resetActivityLogs').toLowerCase()}</p>
            </div>
            <div>
              <label className="text-xs text-[#5C6656]">{t('reports.resetTypeRESET')} <strong>RESET</strong> {t('reports.resetTypeToConfirm')}</label>
              <input value={confirmText} onChange={e => setConfirmText(e.target.value)} className="mt-1 w-full h-10 border border-[#E5E7E1] rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#E2725B]/30" data-testid="reset-confirm-input" autoFocus />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setConfirmOpen(false); setConfirmText(''); }} disabled={submitting} className="border-[#E5E7E1]" data-testid="reset-cancel-btn">{t('reports.resetCancel')}</Button>
              <Button onClick={submit} disabled={submitting || confirmText.trim().toUpperCase() !== 'RESET'} className="bg-[#E2725B] hover:bg-[#C95A44] text-white" data-testid="reset-confirm-btn">
                {submitting ? t('reports.resetResetting') : t('reports.resetYesReset')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
